@if (quote) {
<div class="mx-auto max-w-(--breakpoint-2xl) space-y-6">
    <div class="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
      <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div class="space-y-1">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Quote details</p>
          <h1 class="text-2xl font-semibold text-gray-900">{{ quote.venue?.name || 'Venue' }}</h1>
          @if (quote.venue?.website) {
            <a
              [href]="quote.venue?.website"
              target="_blank"
              rel="noreferrer"
              class="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
            >
              <lucide-icon [name]="GlobeIcon" size="16"></lucide-icon>
              <span class="truncate">{{ quote.venue?.website }}</span>
            </a>
          }
        </div>
        <div class="flex min-w-0 flex-col items-start gap-3 md:flex-row md:items-center">
          <div class="flex min-w-0 flex-wrap gap-3 text-sm text-gray-700">
            <div class="flex min-w-0 max-w-full items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5" [attr.title]="quote.contact_email">
              <lucide-icon [name]="MailIcon" size="16"></lucide-icon>
              <span class="min-w-0 truncate">{{ quote.contact_email }}</span>
            </div>
          </div>

          <!-- TVG-237: staff help panel trigger -->
          <app-help-panel page="quote"></app-help-panel>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div class="lg:col-span-2 rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-100 px-6 py-4">
          <h2 class="text-lg font-semibold text-gray-900">Details</h2>
          <p class="text-sm text-gray-500">Snapshot of the submitted quote.</p>
        </div>
        <div class="px-6 py-5 text-sm text-gray-700">
          <div class="grid grid-cols-1 sm:grid-cols-2">
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 sm:border-r">
              <span class="text-gray-500">{{ displayedDateLabel() }}</span>
              <span class="font-medium text-gray-900">{{ formattedDisplayedDate() }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span class="text-gray-500">Day guests</span>
              <span class="font-medium text-gray-900">{{ quote.day_guests }} people</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 sm:border-r">
              <span class="text-gray-500">Evening guests</span>
              <span class="font-medium text-gray-900">{{ quote.evening_guests }} people</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span class="text-gray-500">Preferred date</span>
              <span class="font-medium text-gray-900">{{ formattedPreferredDate() }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 sm:border-r">
              <span class="text-gray-500">Submitted at</span>
              <span class="font-medium text-gray-900">
                {{ quote.created_at ? (quote.created_at | date: 'd/M/yyyy HH:mm') : '—' }}
              </span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span class="text-gray-500">Status</span>
              <span class="font-medium text-gray-900">{{ quote.status || '—' }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3 sm:col-span-2">
              <span class="text-gray-500">Total amount</span>
              <span class="text-base font-semibold text-gray-900">{{ formattedTotal() }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-100 px-6 py-4">
          <h2 class="text-lg font-semibold text-gray-900">Customer record</h2>
          <p class="text-sm text-gray-500">Contact details from the quote form.</p>
        </div>
        <div class="px-6 py-5 text-sm text-gray-700">
          <div class="divide-y divide-gray-100">
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-gray-500">Name</span>
              <span class="font-medium text-gray-900">{{ quote.contact_name }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-gray-500">Email</span>
              <span class="font-medium text-gray-900">{{ quote.contact_email }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-gray-500">Phone number</span>
              <span class="font-medium text-gray-900">—</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="flex flex-col gap-2 border-b border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Selected items</h3>
          <p class="text-sm text-gray-500">{{ selectedItemsDescription() }}</p>
        </div>
      </div>
      @if (hasDateTabs) {
        <div class="border-b border-gray-100 px-6 py-4">
          <div class="flex flex-wrap gap-2.5">
            @for (row of comparisonRows; track row.date) {
              <button
                type="button"
                class="group inline-flex min-w-[152px] items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all duration-200"
                [class.border-brand-500]="isSelectedDate(row.date)"
                [class.bg-brand-50]="isSelectedDate(row.date)"
                [class.shadow-sm]="isSelectedDate(row.date)"
                [class.ring-1]="isSelectedDate(row.date)"
                [class.ring-brand-200]="isSelectedDate(row.date)"
                [class.text-brand-700]="isSelectedDate(row.date)"
                [class.border-gray-200]="!isSelectedDate(row.date)"
                [class.bg-white]="!isSelectedDate(row.date)"
                [class.text-gray-700]="!isSelectedDate(row.date)"
                [class.hover:border-gray-300]="!isSelectedDate(row.date)"
                [class.hover:bg-gray-50]="!isSelectedDate(row.date)"
                (click)="selectDate(row.date)"
              >
                <span class="truncate text-sm font-semibold leading-5">{{ formattedDateShort(row.date) }}</span>
                <span
                  class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border"
                  [class.border-emerald-300]="tabDateTypeLabel(row.date) === 'Submitted'"
                  [class.bg-emerald-100]="tabDateTypeLabel(row.date) === 'Submitted'"
                  [class.text-emerald-700]="tabDateTypeLabel(row.date) === 'Submitted'"
                  [class.border-slate-300]="tabDateTypeLabel(row.date) !== 'Submitted'"
                  [class.bg-slate-100]="tabDateTypeLabel(row.date) !== 'Submitted'"
                  [class.text-slate-700]="tabDateTypeLabel(row.date) !== 'Submitted'"
                  [appTippy]="tabDateTypeTooltip(row.date)"
                  [attr.aria-label]="tabDateTypeTooltip(row.date)"
                >
                  <lucide-icon [name]="tabDateTypeIcon(row.date)" [size]="14"></lucide-icon>
                </span>
              </button>
            }
          </div>
        </div>
      }
      @if (lineItems.length > 0) {
        <div class="px-6 py-5">
          @if (dayItems.length > 0) {
            <p class="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500" data-testid="line-items-heading-day">Day</p>
            <div class="divide-y divide-gray-100">
              @for (item of dayItems; track item.key) {
                <ng-container [ngTemplateOutlet]="lineItemRow" [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
              }
            </div>
          }
          @if (eveningItems.length > 0) {
            <p class="px-4 pb-2 pt-4 text-xs font-semibold uppercase tracking-wide text-gray-500" data-testid="line-items-heading-evening">Evening</p>
            <div class="divide-y divide-gray-100">
              @for (item of eveningItems; track item.key) {
                <ng-container [ngTemplateOutlet]="lineItemRow" [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
              }
            </div>
          }
          <div class="mt-4 flex items-center justify-between border-t border-gray-200 px-4 pt-4" data-testid="line-items-total">
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Total</p>
            <p class="text-base font-semibold text-gray-900">{{ formattedTotal() }}</p>
          </div>
        </div>
      } @else {
        <div class="px-6 py-8 text-sm text-gray-500">No pricing breakdown available.</div>
      }

      <ng-template #lineItemRow let-item>
        <div class="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div class="flex items-start gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <lucide-icon [name]="MailIcon" size="18"></lucide-icon>
            </div>
            <div>
              <div class="flex flex-wrap items-center gap-2">
                @if (item.product_id) {
                  <a
                    class="text-base font-semibold text-blue-700 hover:underline"
                    [routerLink]="['/products', item.product_id, 'view']"
                  >
                    {{ item.name }}
                  </a>
                } @else {
                  <p class="text-base font-semibold text-gray-900">{{ item.name }}</p>
                }
                @if (item.product_id || item.variant_id || item.option_id) {
                  <span
                    class="inline-flex items-center text-gray-400 cursor-help"
                    [appTippy]="'Product ID: ' + (item.product_id ?? '—') + ' • Variant ID: ' + (item.variant_id ?? '—') + ' • Option ID: ' + (item.option_id ?? '—')"
                  >
                    <lucide-icon [name]="InfoIcon" size="14"></lucide-icon>
                  </span>
                }
              </div>
              <p class="text-sm text-gray-500">
                @if (item.quantity) {
                  {{ item.quantity }} guests
                } @else {
                  —
                }
              </p>
              @if (formatVariantLabel(item)) {
                <p class="text-xs text-gray-500">
                  Variant: {{ formatVariantLabel(item) }}
                </p>
              }
              @if (item.variant_breakdown?.length) {
                <div class="mt-2 space-y-1 text-xs text-gray-500">
                  @for (breakdown of item.variant_breakdown; track breakdown.option_name + '-' + breakdown.option_category + '-' + $index) {
                    <div class="flex items-center justify-between gap-3">
                      <span class="truncate">
                        {{ formatVariantBreakdownLabel(breakdown) }}
                        @if (breakdown.quantity) {
                          - {{ breakdown.quantity }} guests
                        }
                      </span>
                      <span class="shrink-0 text-right">
                        @if (breakdown.unit_price !== null && breakdown.unit_price !== undefined && breakdown.quantity) {
                          {{ breakdown.unit_price | currency: 'GBP':'symbol':'1.0-0' }} × {{ breakdown.quantity }}
                        }
                        @if (breakdown.subtotal !== null && breakdown.subtotal !== undefined) {
                          = {{ breakdown.subtotal | currency: 'GBP':'symbol':'1.0-0' }}
                        }
                      </span>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
          <div class="flex items-center gap-3">
            <app-badge [text]="item.category" color="light"></app-badge>
            <div class="text-right">
              <p class="text-base font-semibold text-gray-900">
                @if (item.subtotal !== null && item.subtotal !== undefined) {
                  {{ item.subtotal | currency: 'GBP':'symbol':'1.0-0' }}
                } @else {
                  —
                }
              </p>
              @if (item.unit_price !== null && item.unit_price !== undefined && item.quantity) {
                <p class="text-xs text-gray-500">
                  {{ item.unit_price | currency: 'GBP':'symbol':'1.0-0' }} × {{ item.quantity }}
                </p>
              }
            </div>
          </div>
        </div>
      </ng-template>
    </div>

    @if (quote.event_id) {
      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-700"
          (click)="goToEventView()"
        >
          View event
        </button>
      </div>
    <!-- TVG-352: raising an event is for admins and managers, so the convert
         actions are hidden from standard users, who keep read access. -->
    } @else if (canCreateEvents && crmSalesFlowEnabled && convertibleDates.length > 0) {
      <!-- TVG-173: one "Convert to event" action per candidate date. Each seeds
           that single date into the Add Event screen; the quote is kept so it
           can be converted again for another date. -->
      <div class="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-100 px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-900">Convert to event</h3>
          <p class="text-sm text-gray-500">Pick a date to create an event for. Convert each date separately to raise more than one.</p>
        </div>
        <div class="flex flex-wrap gap-2.5 px-6 py-4">
          @for (date of convertibleDates; track date) {
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-green-700"
              (click)="goToEventCreate(date)"
            >
              <lucide-icon [name]="CalculatedIcon" [size]="16"></lucide-icon>
              Convert {{ formattedDateShort(date) }}
            </button>
          }
        </div>
      </div>
    } @else if (canCreateEvents) {
      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-green-700"
          (click)="goToEventCreate()"
        >
          Create event
        </button>
      </div>
    }
  </div>
} @else {
  <div class="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
    Loading quote...
  </div>
}
